import {
  useEffect,
  useState
} from 'react'

import {
  motion,
  AnimatePresence
} from 'framer-motion'

import {
  FaPlus,
  FaTrash,
  FaEdit,
  FaCheck,
  FaTimes,
  FaGraduationCap
} from 'react-icons/fa'

import {
  AdminCard,
  Field,
  Input,
  BtnPrimary,
  BtnOutline,
  ColorPicker
} from './AdminUI'

import {
  getEducations,
  addEducation,
  updateEducation,
  deleteEducation
} from '../../../api/portfolioApi'

// ─────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────
const EDU_ICONS = [
  '🎓',
  '🏫',
  '📚',
  '📜',
  '🏛️',
  '✒️',
  '💻',
  '🔬',
  '🏅',
  '🚀'
]

const emptyForm = {
  degree: '',
  institution: '',
  date: '',
  description: '',
  color: 'blue',
  icon: '🎓'
}

// ─────────────────────────────────────────────
// Education Row
// ─────────────────────────────────────────────
function EduRow({
  edu,
  onEdit,
  onDelete
}) {

  const [confirmDel, setConfirmDel] =
    useState(false)

  const colorDot = {
    blue: '#3B82F6',
    green: '#10B981',
    orange: '#F59E0B',
    yellow: '#EAB308',
    purple: '#8B5CF6',
    cyan: '#06B6D4'
  }

  return (

    <motion.div

      layout

      initial={{
        opacity: 0,
        y: 10
      }}

      animate={{
        opacity: 1,
        y: 0
      }}

      exit={{
        opacity: 0,
        x: -20
      }}

      className="
        flex
        items-center
        gap-4
        p-4
        rounded-xl
        bg-white/3
        border
        border-white/5
      "
    >

      {/* Icon */}
      <span
        className="
          text-2xl
          w-8
          text-center
          flex-shrink-0
        "
      >
        {edu.icon}
      </span>

      {/* Content */}
      <div className="flex-1 min-w-0">

        <div className="flex items-center gap-2">

          <span
            className="
              w-2
              h-2
              rounded-full
              flex-shrink-0
            "

            style={{
              background:
                colorDot[edu.color]
                || colorDot.blue
            }}
          />

          <div
            className="
              font-display
              font-semibold
              text-white
              text-sm
              truncate
            "
          >
            {edu.degree}
          </div>

        </div>

        <div
          className="
            font-mono
            text-xs
            text-slate-400
            truncate
            mt-0.5
          "
        >
          {edu.institution}
          {' · '}
          {edu.date}
        </div>

      </div>

      {/* Actions */}
      <div
        className="
          flex
          items-center
          gap-2
          flex-shrink-0
        "
      >

        {confirmDel ? (

          <>

            <span
              className="
                text-xs
                text-red-400
                font-mono
              "
            >
              Delete?
            </span>

            <motion.button

              onClick={() =>
                onDelete(edu.id)
              }

              whileTap={{
                scale: 0.9
              }}

              className="
                w-7
                h-7
                rounded-lg
                bg-red-500/10
                border
                border-red-500/20
                flex
                items-center
                justify-center
                text-red-400
              "
            >

              <FaCheck size={10} />

            </motion.button>

            <motion.button

              onClick={() =>
                setConfirmDel(false)
              }

              whileTap={{
                scale: 0.9
              }}

              className="
                w-7
                h-7
                rounded-lg
                bg-white/5
                border
                border-white/10
                flex
                items-center
                justify-center
                text-slate-400
              "
            >

              <FaTimes size={10} />

            </motion.button>

          </>

        ) : (

          <>

            <motion.button

              onClick={() =>
                onEdit(edu)
              }

              whileTap={{
                scale: 0.9
              }}

              className="
                w-7
                h-7
                rounded-lg
                bg-blue-500/10
                border
                border-blue-500/20
                flex
                items-center
                justify-center
                text-blue-400
              "
            >

              <FaEdit size={10} />

            </motion.button>

            <motion.button

              onClick={() =>
                setConfirmDel(true)
              }

              whileTap={{
                scale: 0.9
              }}

              className="
                w-7
                h-7
                rounded-lg
                bg-white/5
                border
                border-white/10
                flex
                items-center
                justify-center
                text-slate-500
              "
            >

              <FaTrash size={10} />

            </motion.button>

          </>
        )}

      </div>

    </motion.div>
  )
}

// ─────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────
export default function EducationEditor({
  onSaved
}) {

  // ───────────────────────────────────────────
  // State
  // ───────────────────────────────────────────
  const [educations, setEducations] =
    useState([])

  const [loading, setLoading] =
    useState(false)

  const [error, setError] =
    useState('')

  const [form, setForm] =
    useState(emptyForm)

  const [editingId, setEditingId] =
    useState(null)

  const [formOpen, setFormOpen] =
    useState(false)

  // ───────────────────────────────────────────
  // Load Educations
  // ───────────────────────────────────────────
  useEffect(() => {
    loadEducations()
  }, [])

  const loadEducations =
    async () => {

      try {

        setLoading(true)

        const data =
          await getEducations()

        setEducations(
          data || []
        )

      } catch (err) {

        console.error(err)

        setError(
          'Failed to load educations'
        )

      } finally {

        setLoading(false)
      }
    }

  // ───────────────────────────────────────────
  // Update Field
  // ───────────────────────────────────────────
  const updateField = (
    key,
    value
  ) => {

    setForm(prev => ({
      ...prev,
      [key]: value
    }))
  }

  // ───────────────────────────────────────────
  // Add
  // ───────────────────────────────────────────
  const openAdd = () => {

    setForm(emptyForm)

    setEditingId(null)

    setFormOpen(true)
  }

  // ───────────────────────────────────────────
  // Edit
  // ───────────────────────────────────────────
  const openEdit = (edu) => {

    setForm({

      degree:
        edu.degree ?? '',

      institution:
        edu.institution ?? '',

      date:
        edu.date ?? '',

      description:
        edu.description ?? '',

      color:
        edu.color ?? 'blue',

      icon:
        edu.icon ?? '🎓'
    })

    setEditingId(edu.id)

    setFormOpen(true)
  }

  // ───────────────────────────────────────────
  // Save
  // ───────────────────────────────────────────
  const handleSave = async () => {

    if (
      !form.degree.trim()
      || !form.institution.trim()
      || !form.date.trim()
    ) {
      return
    }

    try {

      setLoading(true)

      setError('')

      // Update
      if (editingId) {

        await updateEducation(
          editingId,
          form
        )

        setEducations(prev =>
          prev.map(edu =>
            edu.id === editingId
              ? {
                  ...edu,
                  ...form
                }
              : edu
          )
        )

        onSaved?.(
          'Education updated!'
        )

      }

      // Add
      else {

        const created =
          await addEducation(form)

        setEducations(prev => [
          ...prev,
          created.education
        ])

        onSaved?.(
          'Education added!'
        )
      }

      // Reset
      setForm(emptyForm)

      setEditingId(null)

      setFormOpen(false)

    } catch (err) {

      console.error(err)

      setError(
        err.message ||
        'Failed to save education'
      )

    } finally {

      setLoading(false)
    }
  }

  // ───────────────────────────────────────────
  // Delete
  // ───────────────────────────────────────────
  const handleDelete = async (id) => {

    try {

      setLoading(true)

      await deleteEducation(id)

      setEducations(prev =>
        prev.filter(
          edu => edu.id !== id
        )
      )

      onSaved?.(
        'Education deleted.'
      )

    } catch (err) {

      console.error(err)

      setError(
        err.message ||
        'Failed to delete education'
      )

    } finally {

      setLoading(false)
    }
  }

  // ───────────────────────────────────────────
  // UI
  // ───────────────────────────────────────────
  return (

    <AdminCard

      degree="Educations"

      subdegree={`
        ${educations.length}
        educations
      `}

      action={
        <BtnPrimary
          onClick={openAdd}
          small
        >

          <FaPlus size={10} />

          Add Education

        </BtnPrimary>
      }
    >

      {/* Error */}
      {error && (

        <div
          className="
            mb-4
            rounded-xl
            border
            border-red-500/20
            bg-red-500/10
            px-4
            py-3
            text-sm
            text-red-400
          "
        >
          {error}
        </div>
      )}

      {/* Form */}
      <AnimatePresence>

        {formOpen && (

          <motion.div

            initial={{
              opacity: 0,
              height: 0
            }}

            animate={{
              opacity: 1,
              height: 'auto'
            }}

            exit={{
              opacity: 0,
              height: 0
            }}

            className="
              overflow-hidden
              mb-6
            "
          >

            <div
              className="
                p-5
                rounded-xl
                border
                border-blue-500/20
                bg-blue-500/5
                mb-4
              "
            >

              <h4
                className="
                  font-display
                  font-semibold
                  text-sm
                  text-white
                  mb-4
                  flex
                  items-center
                  gap-2
                "
              >

                <FaGraduationCap
                  size={12}
                  className="text-blue-400"
                />

                {editingId
                  ? 'Edit Education'
                  : 'Add New Education'}

              </h4>

              {/* Icon Picker */}
              <div className="mb-4">

                <label
                  className="
                    block
                    font-mono
                    text-xs
                    text-slate-500
                    mb-2
                    tracking-widest
                  "
                >
                  ICON
                </label>

                <div
                  className="
                    flex
                    flex-wrap
                    gap-2
                  "
                >

                  {EDU_ICONS.map(icon => (

                    <button

                      key={icon}

                      type="button"

                      onClick={() =>
                        updateField(
                          'icon',
                          icon
                        )
                      }

                      className={`
                        w-9
                        h-9
                        rounded-lg
                        text-lg
                        flex
                        items-center
                        justify-center
                        transition-all

                        ${
                          form.icon === icon

                          ? `
                            bg-blue-500/20
                            border-2
                            border-blue-500/50
                            scale-110
                          `

                          : `
                            bg-white/5
                            border
                            border-white/10
                          `
                        }
                      `}
                    >

                      {icon}

                    </button>
                  ))}

                </div>

              </div>

              {/* Inputs */}
              <div
                className="
                  grid
                  grid-cols-1
                  sm:grid-cols-2
                  gap-4
                "
              >

                <Field
                  label="
                    Degree / Title *
                  "
                >

                  <Input
                    value={form.degree}

                    onChange={(e) =>
                      updateField(
                        'degree',
                        e.target.value
                      )
                    }

                    maxLength={100}
                  />

                </Field>

                <Field
                  label="
                    Institution *
                  "
                >

                  <Input
                    value={form.institution}

                    onChange={(e) =>
                      updateField(
                        'institution',
                        e.target.value
                      )
                    }

                    maxLength={80}
                  />

                </Field>

                <Field label="Date *">

                  <Input
                    type="date"
                    value={form.date}
                    required

                    onChange={(e) =>
                      updateField(
                        'date',
                        e.target.value
                      )
                    }

                    maxLength={50}
                  />

                </Field>

                <Field label="Description">

                  <Input
                    value={
                      form.description
                    }

                    onChange={(e) =>
                      updateField(
                        'description',
                        e.target.value
                      )
                    }

                    maxLength={500}
                  />

                </Field>

              </div>

              {/* Color */}
              <Field label="Accent Color">

                <ColorPicker
                  value={form.color}

                  onChange={(value) =>
                    updateField(
                      'color',
                      value
                    )
                  }
                />

              </Field>

              {/* Buttons */}
              <div
                className="
                  flex
                  gap-3
                  mt-2
                "
              >

                <BtnPrimary
                  onClick={handleSave}
                >

                  <FaCheck size={11} />

                  {editingId
                    ? 'Update'
                    : 'Add Education'}

                </BtnPrimary>

                <BtnOutline

                  onClick={() => {

                    setFormOpen(false)

                    setEditingId(null)

                    setForm(emptyForm)
                  }}
                >

                  <FaTimes size={11} />

                  Cancel

                </BtnOutline>

              </div>

            </div>

          </motion.div>
        )}

      </AnimatePresence>

      {/* List */}
      <div className="space-y-2">

        <AnimatePresence>

          {educations.length === 0 ? (

            <div
              className="
                text-center
                py-10
                text-slate-600
                font-mono
                text-sm
              "
            >
              No educations yet.
            </div>

          ) : (

            educations.map(edu => (

              <EduRow

                key={edu.id}

                edu={edu}

                onEdit={openEdit}

                onDelete={handleDelete}

              />
            ))
          )}

        </AnimatePresence>

      </div>

    </AdminCard>
  )
}